import { useMutation } from "@apollo/client";
import { PropsWithChildren } from "react";

import ButtonLink from "@components/typography/ButtonLink";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import { Opener } from "@hooks/useOpener";
import { CarePlanSectionEnum } from "@typing/Enums";
import {
  GetJourneyForMessagesQuery,
  NotificationKindEnum,
  NotificationMarkReadDocument,
  NotificationMarkReadMutation,
  NotificationMarkReadMutationVariables
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Journey = GetJourneyForMessagesQuery["journey"];

type Props = PropsWithChildren<{
  journey: Journey;
  notification: Journey["notifications"][number];
  opener: Opener;
}>;

const NotificationLink = ({ children, journey, notification, opener }: Props) => {
  const myClient = useMyClient();

  const [markNotificationRead] = useMutation<NotificationMarkReadMutation, NotificationMarkReadMutationVariables>(
    NotificationMarkReadDocument
  );

  const linkForNotification = (notification: GetJourneyForMessagesQuery["journey"]["notifications"][number]) => {
    if (notification.kind === NotificationKindEnum.TASK_COMPLETED && notification.resource.__typename === "Task") {
      return journeyRoute({
        journeyId: journey.id,
        linkAfterJourney: `/client-visible-tasks/${notification.resource.id}`,
        myClient
      });
    }
    return journeyRoute({
      journeyId: journey.id,
      linkAfterJourney: `/care-plan?section=${CarePlanSectionEnum.CARE_PLAN}`,
      myClient
    });
  };

  const onClick = () => {
    markNotificationRead({
      variables: {
        id: notification.id
      }
    }).then(() => opener.close());
  };

  const sharedProps = {
    className: "full-width",
    onClick,
    stylized: false
  };

  if (notification.kind === NotificationKindEnum.INTRODUCTION) {
    return <ButtonLink {...sharedProps}>{children}</ButtonLink>;
  }

  return (
    <InternalLink
      {...sharedProps}
      linkEventCreateVariables={{ journeyId: journey.id }}
      to={linkForNotification(notification)}
    >
      {children}
    </InternalLink>
  );
};

export default NotificationLink;
